import { axios } from "@cores/axiosWrapper";

/**
 * Automated Workflow step schema 목록 조회 (페이지 단위).
 * @param {string} stageId - 스테이지 ID
 * @param {string} apiEndpoint - API 엔드포인트
 * @param {string} projectId - 프로젝트 ID
 * @param {object} params - { nextToken, ... } 토큰 기반 페이지네이션
 *   - 전체 누적 조회는 호출 측 thunk(getStepSchemas)에서 nextToken 반복 처리.
 */
export const getAutomatedWorkflowStepSchemasAPI = (stageId, apiEndpoint, projectId, params = {}) => {
    return axios.get(`${apiEndpoint}/automated-workflow-step-schemas`, {
        params,
        headers: {
            stageId,
            projectId,
        },
    });
};

/**
 * Automated Workflow 단건 상세 조회.
 * @param {string} stageId - 스테이지 ID
 * @param {string} apiEndpoint - API 엔드포인트
 * @param {string} projectId - 프로젝트 ID (헤더 필수)
 * @param {string} workflowId - 대상 워크플로우 ID
 */
export const getAutomatedWorkflowAPI = (stageId, apiEndpoint, projectId, workflowId) => {
    return axios.get(`${apiEndpoint}/automated-workflows/${workflowId}`, {
        headers: {
            stageId,
            projectId,
        },
    });
};

/**
 * Automated Workflow 수정 (상태 활성/비활성 토글 포함).
 * @param {string} stageId - 스테이지 ID
 * @param {string} apiEndpoint - API 엔드포인트
 * @param {string} projectId - 프로젝트 ID
 * @param {string} workflowId - 대상 워크플로우 ID
 * @param {object} body - { name, description, status } (status: "ACTIVE" | "INACTIVE")
 *   - description 이 undefined 면 JSON 직렬화 시 제외되어 서버 기존값 보존.
 * - response body 없음(204).
 */
export const updateAutomatedWorkflowAPI = (stageId, apiEndpoint, projectId, workflowId, body) => {
    return axios.patch(`${apiEndpoint}/automated-workflows/${workflowId}`, body, {
        headers: {
            stageId,
            projectId,
        },
    });
};

/**
 * Automated Workflow step tree 전체 덮어쓰기(PUT).
 * @param {string} stageId - 스테이지 ID
 * @param {string} apiEndpoint - API 엔드포인트
 * @param {string} projectId - 프로젝트 ID
 * @param {string} workflowId - 대상 워크플로우 ID
 * @param {object} body - { stepTree } (buildStepTree 결과를 래핑). 기존 step tree 를 전체 교체.
 */
export const updateAutomatedWorkflowStepTreeAPI = (stageId, apiEndpoint, projectId, workflowId, body) => {
    return axios.put(`${apiEndpoint}/automated-workflows/${workflowId}/step-trees`, body, {
        headers: {
            stageId,
            projectId,
        },
    });
};

/**
 * Automated Workflow 유효성 검증(VALIDATE 액션).
 * - POST /automated-workflows/{workflowId}/actions 로 액션 요청.
 * - 요청 본문은 { action: "VALIDATE", data: { stepTree } } 형태로 조립하여 전송.
 * @param {string} stageId - 스테이지 ID
 * @param {string} apiEndpoint - API 엔드포인트
 * @param {string} projectId - 프로젝트 ID
 * @param {string} workflowId - 대상 워크플로우 ID
 * @param {object} stepTree - buildStepTree 결과(START 루트 트리). 검증 대상.
 */
export const validateAutomatedWorkflowAPI = (stageId, apiEndpoint, projectId, workflowId, stepTree) => {
    return axios.post(
        `${apiEndpoint}/automated-workflows/actions`,
        { action: "VALIDATE_STEP_TREE", data: { stepTree } },
        {
            headers: {
                stageId,
                projectId,
            },
        },
    );
};

/**
 * Automated Workflow 삭제.
 * @param {string} stageId - 스테이지 ID
 * @param {string} apiEndpoint - API 엔드포인트
 * @param {string} projectId - 프로젝트 ID
 * @param {string} workflowId - 대상 워크플로우 ID
 */
export const deleteAutomatedWorkflowAPI = (stageId, apiEndpoint, projectId, workflowId) => {
    return axios.delete(`${apiEndpoint}/automated-workflows/${workflowId}`, {
        headers: {
            stageId,
            projectId,
        },
    });
};

/**
 * Automated Workflow 실행이력 목록 조회 (페이지 단위).
 * @param {string} stageId - 스테이지 ID
 * @param {string} apiEndpoint - API 엔드포인트
 * @param {string} projectId - 프로젝트 ID
 * @param {string} workflowId - 대상 워크플로우 ID
 * @param {object} params - { nextToken, ... } 토큰 기반 페이지네이션
 *   - 전체 누적 조회는 호출 측 thunk 에서 nextToken 반복 처리.
 */
export const getAutomatedWorkflowExecutionsAPI = (stageId, apiEndpoint, projectId, workflowId, params = {}) => {
    return axios.get(`${apiEndpoint}/automated-workflows/${workflowId}/executions`, {
        params,
        headers: {
            stageId,
            projectId,
        },
    });
};

/**
 * Automated Workflow 실행이력 단건 상세 조회.
 * @param {string} stageId - 스테이지 ID
 * @param {string} apiEndpoint - API 엔드포인트
 * @param {string} projectId - 프로젝트 ID
 * @param {string} workflowId - 대상 워크플로우 ID
 * @param {string} executionId - 대상 실행이력 ID
 */
export const getAutomatedWorkflowExecutionAPI = (stageId, apiEndpoint, projectId, workflowId, executionId) => {
    return axios.get(`${apiEndpoint}/automated-workflows/${workflowId}/executions/${executionId}`, {
        headers: {
            stageId,
            projectId,
        },
    });
};
